<script lang="ts" setup>
import { ref } from "vue";
import { getAuth, signInWithEmailAndPassword, AuthError } from "firebase/auth";
import { app } from "@/fire";
import { string } from "yup";
import { useForm, useField } from "vee-validate";
import { useI18n } from "vue-i18n";

const props = defineProps({
  email: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["postLogin", "back", "setState"]);

const auth = getAuth(app);
const { t } = useI18n();

const error = ref("");

const showPassword = ref(false);
const isLoading = ref(false);

const { validate, errors } = useForm();
const { value: password } = useField<string>(
  "password",
  string().required(t("login.password.error.missing"))
);

const hasValidated = ref(false);

async function signInWithEmail() {
  if (isLoading.value) {
    return;
  }

  hasValidated.value = true;
  const { valid } = await validate();
  if (!valid) {
    return;
  }

  try {
    isLoading.value = true;

    const result = await signInWithEmailAndPassword(
      auth,
      props.email,
      password.value
    );

    emits("postLogin", {
      user: result.user,
      additionalUserInfo: { isNewUser: false },
    });
  } catch (err: any) {
    if (err.code === "auth/wrong-password") {
      error.value = "invalidPassword";
    } else {
      error.value = "loginFailed";
    }
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="login_container">
    <a class="link top" @click="emits('back')">
      <span class="material-symbols-outlined"> arrow_back </span>
      <span class="text">
        {{ $t("login.back") }}
      </span>
    </a>

    <h2 class="login_header">
      {{ $t("login.passwordInput.header") }}
    </h2>

    <div class="subheader">
      <div>{{ $t("login.passwordInput.subtitle") }}</div>
      <div>{{ email }}</div>
    </div>

    <form @submit.prevent="signInWithEmail" novalidate>
      <div class="input_container">
        <label>{{ $t("login.password.label") }}</label>
        <div class="input_field_container">
          <input
            class="input"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('login.password.placeholder')"
            autocomplete="current-password"
            required
          />
          <span
            class="material-symbols-outlined icon_button"
            @click="showPassword = !showPassword"
          >
            {{ showPassword ? "visibility_off" : "visibility" }}
          </span>
        </div>
        <p
          class="help"
          :class="{ visible: errors.password !== undefined && hasValidated }"
        >
          {{ errors.password }}
        </p>
        <p
          class="help"
          :class="{ visible: error !== undefined && error.length > 0 }"
        >
          {{ $t(`login.passwordInput.error.${error}`) }}
        </p>
      </div>

      <a class="link reset_link" @click="emits('setState', 'RESET_PASSWORD')">
        {{ $t("login.passwordInput.resetLink") }}
      </a>
      <button class="primary button" type="submit" v-if="!isLoading">
        {{ $t("login.passwordInput.submit") }}
      </button>
      <button class="primary button disabled" v-else>
        {{ $t("login.passwordInput.loading") }}
      </button>
    </form>
  </div>
</template>

<style src="./login.scss" scoped lang="scss"></style>

<style lang="scss" scoped>
.subheader {
  text-align: center;
  line-height: 1.8;
  margin-bottom: 1.5em;
}

.button.primary {
  margin-top: 2.5em;
}

.reset_link {
  font-size: 0.9em;
  text-decoration: none;
  margin-top: 0.8em;
}

.reset_link:hover {
  text-decoration: underline;
}
</style>
